/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { SupabaseClient } from '@supabase/supabase-js';

import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import {
  PostHogEventName,
  usePostHogTracking,
} from '@/hooks/usePostHogTracking';
import { createSupabaseClient } from '@/integrations/supabase/client';
import { ProductWithSharing } from '@/types/collaboration';
import {
  CreateProductRequest,
  Product,
  ProductsHookReturn,
  UpdateProductRequest,
} from '@/types/product';
import { handleError } from '@/utils/errorHandling';

interface OwnProduct {
  id: string;
  user_id: string;
  name: string;
  description: string;
  context_urls: null;
  context_documents: [];
  created_at: string;
  updated_at: string;
  product_shares: [];
  is_shared: boolean;
  share_count: number;
  user_role: 'creator' | 'read-only' | 'full-access';
}

export const useProducts = (): ProductsHookReturn => {
  const [products, setProducts] = useState<ProductWithSharing[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { getToken, user, isLoaded } = useAuth();
  const { trackEvent } = usePostHogTracking();

  const supabase: SupabaseClient = useMemo(
    () => createSupabaseClient(getToken),
    [getToken]
  );

  const fetchProducts = useCallback(
    async (name?: string, excludedIds: string[] = []): Promise<void> => {
      try {
        setLoading(true);

        if (!isLoaded) {
          return;
        }

        if (!user) {
          setProducts([]);
          return;
        }

        let ownedQuery = supabase
          .from('products')
          .select(`*, product_shares(user_id, role_id)`)
          .eq('user_id', user.id)
          .like('name', `%${name || ''}%`)
          .order('created_at', { ascending: false });

        if (excludedIds.length > 0) {
          const inList = `(${excludedIds.map((id) => `"${id}"`).join(',')})`;
          ownedQuery = ownedQuery.not('id', 'in', inList);
        }

        const { data: ownedProducts, error: ownedError } = await ownedQuery;
        if (ownedError) {
          console.error(
            'useProducts - Error fetching owned products:',
            ownedError
          );
          throw ownedError;
        }

        const ownedWithSharing: ProductWithSharing[] = (
          ownedProducts || []
        ).map((product: OwnProduct) => ({
          ...product,
          is_shared: (product.product_shares || []).length > 0,
          share_count: (product.product_shares || []).length,
          user_role: 'creator' as const,
        }));

        const allExcludedIds = excludedIds.concat(
          ownedWithSharing.map((product) => product.id)
        );

        let sharedQuery = supabase
          .from('product_shares')
          .select(
            `product_id, role_id, products!inner(*), product_share_roles!inner(name)`
          )
          .eq('user_id', user.id);

        if (name && name.trim().length > 0) {
          sharedQuery = sharedQuery.ilike('products.name', `%${name}%`);
        }

        if (allExcludedIds.length > 0) {
          const inList = `(${allExcludedIds.map((id) => `"${id}"`).join(',')})`;
          sharedQuery = sharedQuery.not('product_id', 'in', inList);
        }

        const { data: sharedProducts, error: sharedError } = await sharedQuery;

        if (sharedError) {
          console.error(
            'useProducts - Error fetching shared products:',
            sharedError
          );
          throw sharedError;
        }

        const sharedWithRole = (sharedProducts || []).map((share) => {
          const productShareRoles = share.product_share_roles as unknown as {
            name: string;
          };
          const roleName = productShareRoles?.name;

          return {
            ...share.products,
            is_shared: true,
            share_count: 0,
            user_role:
              roleName === 'read_only'
                ? ('read-only' as const)
                : ('full-access' as const),
          };
        });

        const allProducts = [...ownedWithSharing, ...sharedWithRole];

        const uniqueProducts = Array.from(
          new Map(
            allProducts.map((product: ProductWithSharing) => [
              product.id,
              product,
            ])
          ).values()
        );

        setProducts(uniqueProducts);
      } catch (error) {
        console.error('useProducts - Error in fetchProducts:', error);
        handleError(error, { action: 'fetch products', context: 'api' });
      } finally {
        setLoading(false);
      }
    },
    [isLoaded, user, supabase]
  );

  const createProduct = useCallback(
    async (productData: CreateProductRequest): Promise<Product | null> => {
      try {
        if (!user) throw new Error('User not authenticated');

        const { data, error } = await supabase
          .from('products')
          .insert([{ ...productData, user_id: user.id }])
          .select()
          .single();

        if (error) throw error;

        const newProduct = data as Product;
        const newProductWithSharing: ProductWithSharing = {
          ...newProduct,
          is_shared: false,
          share_count: 0,
          user_role: 'creator' as const,
        };

        setProducts((prev) => [newProductWithSharing, ...prev]);

        toast({
          title: 'Product created',
          description: `${newProduct.name} has been created successfully.`,
        });

        trackEvent(PostHogEventName.UserFirstProductCreated, {
          product_name: newProduct.name,
        });

        return newProduct;
      } catch (error) {
        handleError(error, { action: 'create product', context: 'api' });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [user, supabase, toast, trackEvent]
  );

  const updateProduct = useCallback(
    async (productData: UpdateProductRequest): Promise<Product | null> => {
      try {
        const { data, error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', productData.id)
          .select()
          .single();

        if (error) throw error;

        const updatedProduct = data as Product;
        setProducts((prev) =>
          prev.map((product) =>
            product.id === updatedProduct.id
              ? { ...product, ...updatedProduct }
              : product
          )
        );
        toast({
          title: 'Product updated',
          description: `${updatedProduct.name} has been updated successfully.`,
        });

        return updatedProduct;
      } catch (error) {
        handleError(error, { action: 'update product', context: 'api' });
        return null;
      }
    },
    [supabase, toast]
  );

  const deleteProduct = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { error } = await supabase.rpc('delete_product_and_dependents', {
          p_product_id: id,
        });

        if (error) throw error;

        setProducts((prev) => prev.filter((product) => product.id !== id));
        toast({
          title: 'Product deleted',
          description: 'Product and related data were removed.',
        });

        return true;
      } catch (error) {
        handleError(error, { action: 'delete product', context: 'api' });
        return false;
      }
    },
    [supabase, toast]
  );

  useEffect(() => {
    if (isLoaded) {
      fetchProducts();
    }
  }, [isLoaded, user?.id, fetchProducts]);

  return {
    data: products,
    products,
    loading,
    createProduct,
    updateProduct,
    deleteProduct,
    refetch: fetchProducts,
  };
};
